import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { CountryRiskLevel } from '@/types/core/country-risk';

const VARIANTS = { LOW: 'green', STANDARD: 'orange', HIGH: 'red' } as const;

type CountryRiskBadgeProps = {
  risk?: CountryRiskLevel | null;
  context?: 'recorded' | 'current';
};

export function CountryRiskBadge({ risk, context }: CountryRiskBadgeProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const label = t(
    `countryRisk.${risk ?? (context === 'recorded' ? 'notRecorded' : 'unavailable')}`,
  );
  const variant = risk ? VARIANTS[risk] : 'gray';

  if (!context) {
    return <Badge variant={variant}>{label}</Badge>;
  }

  const description = t(
    `countryRisk.${context === 'recorded' ? 'recordedLabel' : 'currentLabel'}`,
  );

  return (
    <Tooltip onOpenChange={setOpen} open={open}>
      <TooltipTrigger asChild>
        <Badge asChild variant={variant}>
          <button
            aria-label={`${description}: ${label}`}
            className='min-h-6 cursor-help touch-manipulation'
            onClick={(event) => {
              event.preventDefault();
              setOpen(!open);
            }}
            type='button'
          >
            {label}
          </button>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{description}</TooltipContent>
    </Tooltip>
  );
}
